import CustomReportChannel from '@/Enums/ReportBuilder/ReportChannel.enum';
import type { IControlOption, IGroupedControlOption } from '@/Interfaces/ControlOption.interface';
import AttributionFootTrafficGraphReport from '@/Interfaces/PdfBuilder/FilterValues/AttributionFootTrafficGraphReport.enum';
import AttributionWebVisitsGraphReport from '@/Interfaces/PdfBuilder/FilterValues/AttributionWebVisitsGraphReport.enum';
import AttributionWebVisitsTableReport from '@/Interfaces/PdfBuilder/FilterValues/AttributionWebVisitsTableReport.enum';
import DeliveryGraphReport from '@/Interfaces/PdfBuilder/FilterValues/DeliveryGraphReport.enum';
import DeliveryTableReport from '@/Interfaces/PdfBuilder/FilterValues/DeliveryTableReport.enum';
import PlatformValues from '@/Interfaces/PdfBuilder/FilterValues/PlatformValues.enum';
import { TAB_LABELS } from '@/constants/reportBuilder';

export const platformOptions: IControlOption[] = [
  {
    value: PlatformValues.STV,
    label: TAB_LABELS[CustomReportChannel.Beeswax],
  },
  {
    value: PlatformValues.YouTube,
    label: TAB_LABELS[CustomReportChannel.Youtube],
  },
  {
    value: PlatformValues.Audio,
    label: 'Audio',
  },
  {
    value: PlatformValues.DOOH,
    label: 'Digital Out of Home',
  },
  {
    value: PlatformValues.Simplifi,
    label: 'Place Based & Keyword',
  },
  {
    value: PlatformValues.GAM,
    label: 'GAM',
  },
];

const deliveryGraphReportOptions: IGroupedControlOption[] = [
  { label: 'Impressions', value: DeliveryGraphReport.Impressions },
  { label: 'Reach', value: DeliveryGraphReport.Reach },
  { label: 'Frequency', value: DeliveryGraphReport.Frequency },
  { label: 'Video Completion Rate', value: DeliveryGraphReport.VCR },
  { label: 'Listen-Through Rate', value: DeliveryGraphReport.LTR },
  { label: 'Impressions by Day', value: DeliveryGraphReport.ImpressionsByDay },
  { label: 'Digital Channel', value: DeliveryGraphReport.DigitalChannel },
  { label: 'Map', value: DeliveryGraphReport.Map },
  { label: 'Device Type', value: DeliveryGraphReport.DeviceType },
  { label: 'Campaign Name', value: DeliveryGraphReport.CampaignName },
  { label: 'Creative', value: DeliveryGraphReport.Creative },
  { label: 'Publisher Name', value: DeliveryGraphReport.PublisherName },
  { label: 'Days of Week', value: DeliveryGraphReport.DayOfWeek },
  { label: 'Daypart', value: DeliveryGraphReport.Daypart },
].map(option => ({ ...option, groupLabel: 'Graphs' }));

const deliveryTableReportOptions: IGroupedControlOption[] = [
  { label: 'Date', value: DeliveryTableReport.Date },
  { label: 'Channel', value: DeliveryTableReport.Channel },
  { label: 'Device', value: DeliveryTableReport.Device },
  { label: 'Advertiser', value: DeliveryTableReport.Advertiser },
  { label: 'Campaign', value: DeliveryTableReport.Campaign },
  { label: 'Placement', value: DeliveryTableReport.Placement },
  { label: 'Creative', value: DeliveryTableReport.Creative },
  { label: 'Publisher', value: DeliveryTableReport.Publisher },
  { label: 'Day of Week', value: DeliveryTableReport.DayOfWeek },
  { label: 'Daypart', value: DeliveryTableReport.Daypart },
  { label: 'Genre', value: DeliveryTableReport.Genre },
  { label: 'Venue Type', value: DeliveryTableReport.VenueType },
  { label: 'Deals', value: DeliveryTableReport.Deals },
  { label: 'Geo State', value: DeliveryTableReport.GeoState },
  { label: 'Geo Market', value: DeliveryTableReport.GeoDMA },
  { label: 'Geo County', value: DeliveryTableReport.GeoCounty },
  { label: 'Geo Zip', value: DeliveryTableReport.GeoZip },
  { label: 'Geo Congressional Districts', value: DeliveryTableReport.GeoCongressionalDistrict },
  { label: 'Geo City', value: DeliveryTableReport.GeoCity },
  { label: 'Geo Fence', value: DeliveryTableReport.GeoFence },
].map(option => ({ ...option, groupLabel: 'Tables' }));

export const deliveryReportOptions: IGroupedControlOption[] = [
  ...deliveryGraphReportOptions,
  ...deliveryTableReportOptions,
];

const attributionWebVisitsGraphReportOptions: IGroupedControlOption[] = [
  { label: 'Average Impressions', value: AttributionWebVisitsGraphReport.AverageImpressions },
  { label: 'Digital Channel', value: AttributionWebVisitsGraphReport.DigitalChannel },
  { label: 'Device Type', value: AttributionWebVisitsGraphReport.DeviceType },
  { label: 'Campaign Name', value: AttributionWebVisitsGraphReport.CampaignName },
  { label: 'Creative', value: AttributionWebVisitsGraphReport.Creative },
  { label: 'Publisher Name', value: AttributionWebVisitsGraphReport.PublisherName },
].map(option => ({ ...option, groupLabel: 'Web Visits Graphs' }));

const attributionWebVisitsTableReportOptions: IGroupedControlOption[] = [
  { label: 'Date', value: AttributionWebVisitsTableReport.Date },
  { label: 'Channel', value: AttributionWebVisitsTableReport.Channel },
  { label: 'Device', value: AttributionWebVisitsTableReport.Device },
  { label: 'Campaign', value: AttributionWebVisitsTableReport.Campaign },
  { label: 'Placement', value: AttributionWebVisitsTableReport.Placement },
  { label: 'Pixels', value: AttributionWebVisitsTableReport.Pixels },
  { label: 'Creative', value: AttributionWebVisitsTableReport.Creative },
  { label: 'Publisher', value: AttributionWebVisitsTableReport.Publisher },
  { label: 'Day of Week', value: AttributionWebVisitsTableReport.DayOfWeek },
  { label: 'Daypart', value: AttributionWebVisitsTableReport.Daypart },
].map(option => ({ ...option, groupLabel: 'Web Visits Tables' }));

const attributionFootTrafficGraphReportOptions: IGroupedControlOption[] = [
  { label: 'Campaign Impressions', value: AttributionFootTrafficGraphReport.CampaignImpressions },
  { label: 'Total Store Visits', value: AttributionFootTrafficGraphReport.TotalStoreVisits },
  // MEQ: backend does not provide this value yet — option re-enabled when source is wired up.
  // { label: 'MEQ', value: AttributionFootTrafficGraphReport.MEQ },
  { label: 'On-Air / Off-Air Lift %', value: AttributionFootTrafficGraphReport.Lift },
  {
    label: 'Impressions and Foot Traffic Visits',
    value: AttributionFootTrafficGraphReport.ImpressionsAndFootTrafficVisits,
  },
  // Gender: backend does not provide this data yet — option re-enabled when source is wired up.
  // { label: 'Gender', value: AttributionFootTrafficGraphReport.Gender },
  { label: 'Day of Week', value: AttributionFootTrafficGraphReport.DayOfWeek },
  { label: 'Hour of Day', value: AttributionFootTrafficGraphReport.HourOfDay },
].map(option => ({ ...option, groupLabel: 'Foot Traffic Graphs' }));

export const attributionReportOptions: IGroupedControlOption[] = [
  ...attributionWebVisitsGraphReportOptions,
  ...attributionWebVisitsTableReportOptions,
  ...attributionFootTrafficGraphReportOptions,
];
